import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Tag,
  Table,
  Typography,
  Divider,
  Modal,
  Empty,
} from "antd";
import {
  LoginOutlined,
  LogoutOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import useAttendance from "../utils/useAttendance.js";
import "./AttendanceManagement.css";
import AttendanceRegularizationDialog from "../components/AttendanceRegularizationModal.jsx";

const { Title, Text } = Typography;

/* ── helpers ─────────────────────────────────────────── */
const toYMD = (d) => new Date(d).toISOString().slice(0, 10);

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

/* ── component ───────────────────────────────────────── */
export default function AttendanceManagement() {
  const {
    isClockedIn,
    punchIn,
    punchOut,
    activeShift,
    history,
    month,
    setMonth,
    fetchHistory,
    loading,
  } = useAttendance();

  const [tick, setTick] = useState(Date.now());
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    action: null,
  });
  const [openCorrection, setOpenCorrection] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  /* ── derived values ──────────────────────────────────── */
  const todayKey = toYMD(new Date());

  const todayRow = useMemo(() => {
    const row = history.find((r) => r.date === todayKey);
    if (row) return row;
    if (activeShift && toYMD(activeShift?.loginTime) === todayKey) {
      return {
        id: "today-virtual",
        date: todayKey,
        punchIn: activeShift?.loginTime,
        punchOut: null,
        durationMs: tick - activeShift?.loginTime,
        status: "In Progress",
      };
    }
    return null;
  }, [history, activeShift, tick, todayKey]);

  const monthKey = new Date().toISOString().slice(0, 7);
  const monthRows = history.filter((r) => r.date.startsWith(monthKey));
  const monthHoursMs = monthRows.reduce(
    (acc, r) => acc + (r.durationMs || 0),
    0,
  );
  const monthDays = new Set(monthRows.map((r) => r.date)).size;

  const elapsedMs = isClockedIn
    ? tick - new Date(activeShift?.loginTime)
    : null;

  /* ── confirm modal handlers ─────────────────────────── */
  const openConfirm = (action) => setConfirmModal({ open: true, action });

  const handleConfirm = () => {
    if (confirmModal.action === "punchIn") punchIn();
    if (confirmModal.action === "punchOut") punchOut();
    setConfirmModal({ open: false, action: null });
  };

  const handleConfirmCancel = () =>
    setConfirmModal({ open: false, action: null });

  /* ── table columns ───────────────────────────────────── */
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (val) => (
        <Text style={{ color: "#13487D", fontWeight: 600 }}>{val}</Text>
      ),
    },
    {
      title: "Punch In",
      dataIndex: "loginTime",
      key: "loginTime",
      render: (val) =>
        val ? (
          <span style={{ color: "#3699FF", fontWeight: 500 }}>
            {moment(val).format("hh:mm A")}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Punch Out",
      dataIndex: "logoutTime",
      key: "logoutTime",
      render: (val) =>
        val ? (
          <span style={{ color: "#13487D", fontWeight: 500 }}>
            {moment(val).format("hh:mm A")}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Duration",
      dataIndex: "workingHour",
      key: "workingHour",
      render: (val) =>
        val ? (
          <Tag icon={<ClockCircleOutlined />} color="blue">
            {val}h
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        const isPresent = val === "Present";
        return (
          <Tag
            icon={isPresent ? <CheckCircleOutlined /> : <WarningOutlined />}
            color={isPresent ? "success" : "warning"}
          >
            {val || "—"}
          </Tag>
        );
      },
    },
  ];

  /* ── render ──────────────────────────────────────────── */
  return (
    <div className="attendance-page">
      <Row gutter={[20, 20]}>
        {/* ── LEFT: Clock panel ───────────────────────── */}
        <Col xs={24} md={10}>
          <Card className="attendance-card">
            {/* Header */}
            <div className="att-card-header">
              <Title level={5} className="att-title">
                Attendance
              </Title>
              <Tag
                color={isClockedIn ? "success" : "default"}
                className="att-status-tag"
              >
                {isClockedIn ? "● Clocked In" : "○ Clocked Out"}
              </Tag>
            </div>

            {/* Live clock */}
            <div className="att-clock">
              {new Date(tick).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </div>
            <Text type="secondary" className="att-date">
              <CalendarOutlined style={{ marginRight: 6 }} />
              {new Date().toLocaleDateString("en-IN", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </Text>

            <Divider style={{ margin: "20px 0" }} />

            {/* Punch buttons */}
            <div className="att-btn-row">
              <Button
                type="primary"
                icon={<LoginOutlined />}
                disabled={isClockedIn || !!activeShift}
                loading={loading}
                onClick={() => openConfirm("punchIn")}
                className="att-btn att-btn-in"
                size="large"
              >
                Punch In
              </Button>
              <Button
                type="primary"
                icon={<LogoutOutlined />}
                disabled={!isClockedIn}
                loading={loading}
                onClick={() => openConfirm("punchOut")}
                className="att-btn att-btn-out"
                size="large"
              >
                Punch Out
              </Button>
            </div>

            {/* Elapsed timer */}
            <div className="att-elapsed-box">
              <Text className="att-elapsed-label">
                {isClockedIn ? "Currently working" : "Not working right now"}
              </Text>
              <div className="att-elapsed-timer">{fmtDuration(elapsedMs)}</div>
              <div className="att-shift-chips">
                <Tag>
                  In:{" "}
                  {activeShift?.loginTime
                    ? moment(activeShift.loginTime).format("hh:mm A")
                    : "—"}
                </Tag>
                <Tag>
                  Out:{" "}
                  {activeShift?.logoutTime
                    ? moment(activeShift.logoutTime).format("hh:mm A")
                    : "—"}
                </Tag>
              </div>
            </div>

            {/* Today snapshot */}
            <div style={{ marginTop: 20 }}>
              <Text
                strong
                style={{ display: "block", marginBottom: 10, color: "#13487D" }}
              >
                Today
              </Text>
              <div className="att-today-grid">
                {[
                  {
                    label: "Punch In",
                    value: activeShift?.loginTime
                      ? moment(activeShift.loginTime).format("hh:mm A")
                      : "—",
                  },
                  {
                    label: "Punch Out",
                    value: activeShift?.logoutTime
                      ? moment(activeShift.logoutTime).format("hh:mm A")
                      : "—",
                  },
                  {
                    label: "Total",
                    value: activeShift?.workingHour
                      ? `${activeShift.workingHour}h`
                      : "—",
                  },
                  {
                    label: "Status",
                    value: activeShift?.status || "Pending",
                  },
                ].map((item) => (
                  <div key={item.label} className="att-today-item">
                    <Text type="secondary" className="att-today-label">
                      {item.label}
                    </Text>
                    <Text strong className="att-today-value">
                      {item.value}
                    </Text>
                  </div>
                ))}
              </div>
            </div>

            {/* Month summary */}
            <div style={{ marginTop: 20 }}>
              <Text
                strong
                style={{ display: "block", marginBottom: 10, color: "#13487D" }}
              >
                This Month
              </Text>
              <div style={{ display: "flex", gap: 10 }}>
                <Tag icon={<CalendarOutlined />} color="blue">
                  Days: {monthDays}
                </Tag>
                <Tag icon={<ClockCircleOutlined />} color="geekblue">
                  Hours: {fmtDuration(monthHoursMs)}
                </Tag>
              </div>
            </div>
          </Card>
        </Col>

        {/* ── RIGHT: History table ─────────────────────── */}
        <Col xs={24} md={14}>
          <Card className="attendance-card">
            <div className="att-history-header">
              <Title level={5} className="att-title">
                Attendance History
              </Title>
              <div className="att-history-controls">
                <Button onClick={() => setOpenCorrection(true)}>
                  Correction
                </Button>
                <input
                  type="month"
                  value={month}
                  min="2025-10"
                  max={moment().format("YYYY-MM")}
                  onChange={(e) => setMonth(e.target.value)}
                  className="att-month-input"
                />
              </div>
            </div>

            <Table
              dataSource={history}
              columns={columns}
              rowKey={(r, i) => r.id || r.date || i}
              pagination={false}
              loading={loading}
              scroll={{ y: 460 }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No records yet. Punch in to get started."
                  />
                ),
              }}
              rowClassName={(r) => (r.date === todayKey ? "att-today-row" : "")}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Confirm Modal ─────────────────────────────── */}
      <Modal
        open={confirmModal.open}
        title="Are you sure?"
        onOk={handleConfirm}
        onCancel={handleConfirmCancel}
        okText="Yes"
        cancelText="No"
        okButtonProps={{
          style: { background: "#3699FF", borderColor: "#3699FF" },
        }}
      >
        <Text>
          Are you sure you want to{" "}
          <Text strong>
            {confirmModal.action === "punchIn" ? "Punch In" : "Punch Out"}
          </Text>
          ?
        </Text>
      </Modal>

      {/* ── Correction Dialog ─────────────────────────── */}
      {/* Replace with your AttendanceRegularizationDialog equivalent */}
      <AttendanceRegularizationDialog
        open={openCorrection}
        onClose={() => setOpenCorrection(false)}
      />
    </div>
  );
}
